import React from 'react';

import { IS_LOCAL, REQ_SOURCE, getTicketsUrl, isDefined } from '@nfctron/nexus-core';
import { NfctronTextLogo, UnitedTicketsLogo } from '@nfctron/nexus-react-svg';

import { type DnsHost, type DnsHostConfigResolved, getDnsHostConfig } from '@root/config/dns.config';

/**
 * Base configuration for an integrator
 * Contains visual elements like logo
 */
interface IntegratorConfigBase {
	/** React node representing the integrator's logo */
	logoNode: React.ReactNode;
}

/**
 * Complete integrator configuration
 * Extends the base config with DNS host configuration
 */
export interface IntegratorConfig extends IntegratorConfigBase, Omit<DnsHostConfigResolved<DnsHost>, 'hostname' | 'reqSource'> {
	/** The DNS hostname for this integrator, null for root integrator */
	hostname: DnsHost | null;
	reqSource: REQ_SOURCE.WEB | REQ_SOURCE.WEB_UNITED_TICKETS | REQ_SOURCE.WEB_DINO_LIVE;
}

/**
 * Resolved integrator configuration with additional properties
 * Used at runtime after resolution
 */
export interface IntegratorConfigResolved extends IntegratorConfig {
	/** Flag indicating if this is the root (NFCtron) integrator */
	$isRoot: boolean;
}

/**
 * Parameters representing a location
 * Used for matching integrators to hostnames
 */
interface LocationParams {
	/** The hostname part of the URL */
	hostname: string;
	/** The pathname part of the URL */
	pathname: string;
	/** The origin of the URL */
	origin: string;
}

/**
 * Creates an integrator configuration by combining base config with DNS host config
 * @param dnsHost - The DNS host identifier
 * @param integratorConfig - The base integrator configuration
 * @returns Complete integrator configuration
 * @throws Error if DNS config is not found for the hostname
 */
function createIntegratorConfigFrom(dnsHost: DnsHost, integratorConfig: IntegratorConfigBase) {
	const dnsConfig = getDnsHostConfig(dnsHost);
	if (!isDefined(dnsConfig)) {
		throw new Error(`DNS config not found for hostname: ${dnsHost}`);
	}
	return {
		...integratorConfig,
		...dnsConfig,
	};
}

/**
 * Root integrator (NFCtron)
 */
const ROOT_INTEGRATOR = {
	$isRoot: true,
	reqSource: REQ_SOURCE.WEB,
	logoNode: <NfctronTextLogo className="text-lucky-point-500 h-6 w-[100px]" />,
	hostname: null,
	urls: {
		getTermsUrl: null,
		getHomeUrl() {
			return new URL('https://www.nfctron.com');
		},
		getEventUrl(input) {
			const localePath = input.locale ? `/${input.locale}` : '';
			return getTicketsUrl(`${localePath}/event/${input.organizerSlug}/${input.eventSlug}/cart`);
		},
		getEventGroupUrl(input) {
			const localePath = input.locale ? `/${input.locale}` : '';
			return getTicketsUrl(`${localePath}/tour/${input.eventGroupSlug}`);
		},
		getOrganizerUrl(input) {
			const localePath = input.locale ? `/${input.locale}` : '';
			return getTicketsUrl(`${localePath}/organizer/${input.organizerSlug}`);
		},
	},
	rules: {
		robots: [],
		rewrite: [],
		redirect: [],
	},
	scopes: {
		organizers: [],
		eventGroups: [],
	},
} as const satisfies IntegratorConfigResolved;

/**
 * Registered integrators
 */
export const REGISTERED_INTEGRATORS: IntegratorConfig[] = [
	/** universal music / united tickets integrator */
	createIntegratorConfigFrom('store.united-tickets.cz', {
		logoNode: <UnitedTicketsLogo className="h-10 w-20" />,
	}),
	/** mirai-summer-fest-2025 integrator */
	createIntegratorConfigFrom('vstupenky.miraifest.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/7887739f-a786-4232-9df8-cfbdb628793b.png" className="h-10" alt="Mirai Summer Fest" />,
	}),
	/** chinaski-2025 integrator */
	createIntegratorConfigFrom('vstupenky.chinaski.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/d83648ea-d9c0-4a4e-b85f-fa6b6851d402.svg" className="h-10" alt="Chinaski" />,
	}),
	/** marek-ztraceny-je-zpatky-tour-2025 integrator */
	createIntegratorConfigFrom('vstupenky.marekztraceny.cz', {
		logoNode: <img src="https://images.nfctron.com/event-groups/7e5b5a4b-f4fe-4baf-8842-de445b5b0d9b.svg" className="h-14" alt="Marek Ztracený" />,
	}),
	/** hrady-cz-2025 integrator */
	createIntegratorConfigFrom('vstupenky.hradycz.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/39102fc2-8bd0-490d-b60f-261a14268030.svg" className="h-10" alt="HradyCZ" />,
	}),
	/** mklv-fest-2025 integrator */
	createIntegratorConfigFrom('vstupenky.mklvfest.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/ded33363-f5d4-46b7-a96b-830a74218479.png" className="h-10" alt="MKLV Fest" />,
	}),
	/** prague-harley-days-2025 integrator */
	createIntegratorConfigFrom('vstupenky.pragueharleydays.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/e476ba59-a8e7-436d-87aa-0838a59af644.png" className="h-10" alt="Prague Harley Days" />,
	}),
	/** prague-open-air-2025 integrator */
	createIntegratorConfigFrom('vstupenky.pragueopenair.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/39fd15db-0a41-45ac-a920-b3d3ee53f0f0.png" className="h-16" alt="Prague Open Air" />,
	}),
	/** soldout-festival-2025 integrator */
	createIntegratorConfigFrom('vstupenky.soldoutfestival.cz', {
		logoNode: <img src="https://images.nfctron.com/organizers/6d07cf95-7b8a-4ddf-8314-e32a79de978a.png" className="h-14" alt="Soldout Festival" />,
	}),
	/** lovestream-tickets integrator */
	createIntegratorConfigFrom('tickets.lovestream.sk', {
		logoNode: <img src="https://images.nfctron.com/organizers/1fe38b89-e913-4596-b8df-fb3fe9c35590.svg" className="h-6.5" alt="Lovestream Tickets" />,
	}),
	/* SYMPHONIKA ON THE ROCK 2025 */
	createIntegratorConfigFrom('vstupenky.symphonika.cz', {
		logoNode: (
			<img src="https://images.nfctron.com/organizers/16446418-1008-42aa-8874-c92f2fd2c604.png" className="h-10" alt="Symphonika On The Rock 2025" />
		),
	}),
	/** root integrator (NFCtron) */
	ROOT_INTEGRATOR,
] as const;

/**
 * Returns current integrator config based on current location params (hostname)
 * @param currentLocation - Current location params on client side
 * @returns Current integrator config
 */
export function getIntegrator(currentLocation: LocationParams | null): IntegratorConfigResolved {
	const match = REGISTERED_INTEGRATORS.find((orgIntegrator) => {
		if (!isDefined(currentLocation)) return false;
		return orgIntegrator.hostname === currentLocation.hostname;
	});

	/** fallback to nfctron default integrator */
	const configToReturn = match ?? ROOT_INTEGRATOR;
	return {
		$isRoot: false,
		/** current orgIntegrator config */
		...configToReturn,
		/** adjust URLs for local dev */
		...(IS_LOCAL && {
			urls: {
				getHomeUrl() {
					return new URL('http://localhost:4000/sitemap.xml');
				},
				getTermsUrl: null,
				getEventUrl(input) {
					const origin = currentLocation?.origin ?? 'http://localhost:4000';
					const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
					return new URL(`${localePath}/event/${input.organizerSlug}/${input.eventSlug}`, origin);
				},
				getEventGroupUrl(input) {
					const origin = currentLocation?.origin ?? 'http://localhost:4000';
					const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
					return new URL(`${localePath}/tour/${input.eventGroupSlug}`, origin);
				},
				getOrganizerUrl(input) {
					const origin = currentLocation?.origin ?? 'http://localhost:4000';
					const localePath = isDefined(input.locale) ? `/${input.locale}` : '';
					return new URL(`${localePath}/organizer/${input.organizerSlug}`, origin);
				},
			},
		}),
	};
}
